import { useCallback, useState } from 'react';

import { Button, Form, Input, notification, Select, Typography } from 'antd';
import { useForm } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';

import { useAppSelector } from '@/hooks/redux';
import IUser from '@/interfaces/user';
import { useGetCoursesQuery } from '@/services/course';
import helpService from '@/services/help';
import { css, styled, StyledProp } from '@/styled';

type NotificationType = 'success' | 'info' | 'warning' | 'error';

interface IHelpFormValues {
  couHash: string;
  message: string;
  title: string;
}

interface IHelpProps extends StyledProp {
  courseTitle?: string;
  courseHash?: string;
}

const HelpForm = ({ className, courseTitle, courseHash }: IHelpProps) => {
  const { data: response, isLoading } = useGetCoursesQuery({ page: 1, size: 100 });
  const user = useAppSelector(state => state.auth.user as IUser);
  const [api, contextHolder] = notification.useNotification();
  const [submitting, setSubmitting] = useState(false);
  const isCoursePage = Boolean(courseTitle) && Boolean(courseHash);

  const [form] = useForm();

  const openNotificationWithIcon = useCallback(
    (message: string, type: NotificationType) => {
      api[type]({
        message
      });
    },
    [api]
  );

  const sendHelp = useCallback(
    async (values: IHelpFormValues) => {
      setSubmitting(true);

      const hash = courseHash ?? values.couHash;

      try {
        await helpService.saveMessage({
          title: values.title,
          message: values.message,
          cou_hash: hash,
          email: user.email,
          name: user.name
        });
        openNotificationWithIcon('Solicitação enviada com sucesso!', 'success');
        form.resetFields();
      } catch {
        openNotificationWithIcon('Ocorreu um erro ao enviar a solicitação!', 'error');
      }

      setSubmitting(false);
    },
    [openNotificationWithIcon, user.email, user.name, form, courseHash]
  );

  return (
    <div className={className}>
      {contextHolder}

      <Typography.Title level={4} ellipsis={true}>
        Solicitação de ajuda
      </Typography.Title>

      <Form form={form} name='helpForm' layout='vertical' onFinish={sendHelp}>
        <Form.Item
          label='Título'
          name='title'
          rules={[
            { required: true, message: 'O Campo título é obrigatório e precisa ter no mínimo 3 caracteres', min: 3 }
          ]}
        >
          <Input size='large' placeholder='Escreva o título de sua solicitação' />
        </Form.Item>

        <Form.Item
          label='Curso'
          name='couHash'
          rules={[{ required: !isCoursePage, message: 'O Campo curso é obrigatório' }]}
        >
          <Select size='large' placeholder='Selecione um curso' defaultValue={courseTitle} disabled={isCoursePage}>
            <Select.Option key={0} value='0'>
              Dúvidas na plataforma
            </Select.Option>

            {!isLoading &&
              response?.data.map((course, key) => (
                <Select.Option key={key} value={course.hash}>
                  {course.title}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          className='help-form-label'
          label='Mensagem'
          name='message'
          rules={[{ required: true, message: 'O Campo mensagem é obrigatório' }]}
        >
          <TextArea size='large' className='textArea' rows={4} placeholder='Digite sua mensagem' />
        </Form.Item>

        <Form.Item noStyle>
          <Button type='primary' htmlType='submit' size='large' loading={submitting}>
            Enviar solicitação
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};
export default styled(HelpForm)(
  ({ theme }) => css`
    h4.ant-typography {
      font-size: ${theme.space.pxToRem(20)};
      font-weight: 400;
      margin-bottom: 24px;
    }

    [title='Título'],
    [title='Curso'],
    [title='Mensagem'] {
      color: ${theme.colorTextSecondary} !important;
    }

    button,
    input,
    .ant-select-selection-search {
      height: 40px;
    }

    .textArea {
      resize: none;
      min-height: 111px;
    }
  `
);
